import moment from 'moment/moment'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { BaseRecordView } from '../common/sections'
import { PayeeView } from './PayeeView'

const viewWillAppear = async ({ urlParams, owner }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Payee not found')
    }

    const payee = await RecordFactory.getInstance().loadPayee(urlParams.id)
    if (!payee) {
        throw new RecordNotFoundError('Payee not found')
    }

    if (!payee.person || !payee.person.id) {
        throw new RecordNotFoundError('Payee not found')
    }

    const person = await RecordFactory.getInstance().loadUser(payee.person.id)
    if (!person) {
        throw new RecordNotFoundError('Payee not found')
    }

    const userAPIResponse = await RecordFactory.getInstance().fetchUser(payee.person.id)
    if (!userAPIResponse?.ok) {
        throw new RecordNotFoundError('Payee not found')
    }

    const metadata = {
        paymentsAmount: userAPIResponse.value.payments ?? 0,
        lastJobDate: userAPIResponse.value.last_job ? moment(userAPIResponse.value.last_job) : null,
        nextJobDate: userAPIResponse.value.next_job ? moment(userAPIResponse.value.next_job) : null,
        unpaidJobsAmount: userAPIResponse.value.open_jobs ?? 0,
    }

    const skillItems = (
        owner.id !== undefined ? await RecordFactory.getInstance().loadItems(owner.id) : []
    ).filter((item) => item.id !== undefined && payee.skills.includes(item.id))

    return { payee, person, metadata, skillItems }
}

const render = ({ data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { payee, person, metadata, skillItems } = data

    const navigate = useNavigate()

    usePageTitle(`${person.name}`)

    return (
        <BaseRecordView
            title="Team member"
            headerButtons={[
                { title: 'View history', onClick: () => navigate(`/dashboard/user/${person.id}`) },
            ]}
            backButton={{ title: 'Team', onClick: () => navigate(`/dashboards/employees`) }}
        >
            <PayeeView payee={payee} person={person} metadata={metadata} skillItems={skillItems} />
        </BaseRecordView>
    )
}

export const PayeePage = usePSComponent(render, viewWillAppear)
